import { BdUserFile } from '../file-models/file';
import {
  BdResourceService,
  BdSharePermission,
} from '@bindoc-projects/resource-domain-lib';
import { EMPTY, map, Observable, switchMap } from 'rxjs';
import {
  BdCondition,
  BdTrueCondition,
} from '@bindoc-projects/condition-domain-lib';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { UserFilesApi } from './files.store';
import {
  CacheService,
  ResourceConfigControlledService,
  ResourceConfigItem,
} from '@bindoc-projects/resource-lib/data';

/** A user file as the API delivers it: the dates are ISO strings. */
type BdUserFileRaw = Omit<BdUserFile, 'modifiedDate' | 'createdDate'> & {
  modifiedDate?: string;
  createdDate?: string;
};

/**
 * Turns the API's ISO date strings into the `Date`s that `BdResource` promises —
 * the resource info menu calls `modifiedDate.toISOString()` (BDA-6859).
 */
function toUserFile(raw: BdUserFileRaw): BdUserFile {
  return {
    ...raw,
    modifiedDate: raw.modifiedDate ? new Date(raw.modifiedDate) : undefined,
    createdDate: raw.createdDate ? new Date(raw.createdDate) : undefined,
  };
}

export class UserFileService
  extends ResourceConfigControlledService
  implements BdResourceService<BdUserFile>
{
  private _api: UserFilesApi;

  constructor(
    resourceConfig: ResourceConfigItem,
    environmentService: BdAppEnvironmentService,
    cacheService: CacheService,
    private _httpClient: HttpClient,
  ) {
    super(resourceConfig, environmentService, cacheService);

    this._api = new UserFilesApi(this.getBaseUrl());
  }

  public availableLabels(): Observable<string[]> {
    const url = `${this._api.crud()}files/labels/available`;

    return this._httpClient.get<string[]>(url);
  }

  /**
   * DO NOT USE - JUST ADDED TO SATISFY THE INTERFACE
   * For creating a new file use the upload method
   * @deprecated - DO NOT USE - JUST ADDED TO SATISFY THE INTERFACE
   */
  create(): Observable<BdUserFile> {
    throw new Error('Method not implemented.');
  }

  upload(file: File, labels: string[]): Observable<BdUserFile> {
    let url = `${this._api.crud()}files/create`;
    const formData = new FormData();

    if (labels) {
      const labelParams = new HttpParams({
        fromObject: {
          labels: labels,
        },
      });

      url = url.concat('?').concat(labelParams.toString());
    }

    formData.append('file', file);

    return this._httpClient
      .post<BdUserFileRaw>(url, formData)
      .pipe(map(toUserFile));
  }

  public downloadFile(fileId: string): Observable<Blob> {
    const url = `${this._api.crud()}files/${fileId}/download`;

    return this._httpClient.get(url, { responseType: 'blob' });
  }

  findAll(
    minPermission: BdSharePermission | undefined,
    companyId?: string,
    condition: BdCondition = new BdTrueCondition(),
  ): Observable<BdUserFile[]> {
    const url = `${this._api.crud()}files/`;

    let params = new HttpParams();
    if (companyId) {
      params = params.set('companyId', companyId);
    }

    return this._httpClient
      .post<BdUserFileRaw[]>(url, { condition }, { params })
      .pipe(map((files) => files.map(toUserFile)));
  }

  findById(id: string): Observable<BdUserFile> {
    const url = `${this._api.crud()}files/${id}`;

    return this._httpClient.get<BdUserFileRaw>(url).pipe(map(toUserFile));
  }

  /**
   * Renames a file and, when `labels` is a list, replaces its labels with
   * exactly that list. `null` leaves the stored labels untouched.
   */
  update(
    toUpdate: BdUserFile,
    fileName: string,
    labels: string[] | null = null,
  ): Observable<void> {
    const url = `${this._api.crud()}files/${toUpdate.id}/update`;
    const body: {
      fileName: string;
      labels: string[] | null;
    } = {
      fileName,
      labels,
    };

    return this._httpClient.post<BdUserFile>(url, body).pipe(
      switchMap(() => {
        return EMPTY;
      }),
    );
  }
}
